import React, {useMemo, useState} from "react";
import {
    Avatar,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextareaAutosize,
    TextField,
    Tooltip
} from "@material-ui/core";
import {nanoid} from "@reduxjs/toolkit";
import {Card, Col, Row} from "react-bootstrap";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import RemoveIcon from "@material-ui/icons/Remove";
import {orderStates, useStyles} from "../constants";
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import {useDispatch, useSelector} from "react-redux";
import {deleteOrder, updateOrder} from "../../../redux/slices/orders";
import DeleteModal from "./DeleteModal";
import UpdateOrders from "./UpdateOrders";
import CloseIcon from "@material-ui/icons/Close";
import {DateTime} from 'luxon'
import Button from "@material-ui/core/Button";
import ExcelFile from "@ibrahimrahmani/react-export-excel/dist/ExcelPlugin/components/ExcelFile";
import ExcelSheet from "@ibrahimrahmani/react-export-excel/dist/ExcelPlugin/elements/ExcelSheet";
import ExcelColumn from "@ibrahimrahmani/react-export-excel/dist/ExcelPlugin/elements/ExcelColumn";
import GridOnIcon from '@material-ui/icons/GridOn';
import InventoryOrdered from '../../Inventory/InventoryOrdered';
import {selectInventory} from "src/constant/selectors";

import {flattenDeep} from 'lodash'

export default function DetailModal({open, onClose, detail}) {
    const deadline = DateTime.fromJSDate(detail.Order.Deadline.toDate());
    const now = DateTime.now()
    const isLate = deadline < now;

    const classes = useStyles();
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
    const [isUpdateModalShown, setIsUpdateModalShown] = useState(false)
    const {shipping, pos} = useSelector(selectInventory);
    const meta = useMemo(() => {

        let first = [...shipping, ...pos];

        let second = [];


        while (first.length !== 0) {
            const selected = first[0];
            const duplicate = first.filter(one => one?.id === selected?.id);
            first = first.filter(one => one?.id !== selected?.id);

            const purchases = flattenDeep(duplicate.map(one => one?.pos || []))
            const shipping = flattenDeep(duplicate.map(one => one?.shipping || []))

            const countList = [...purchases,...shipping].map(one => Number(one?.quantity || 0));
            const totalQuantity = (countList.length === 0) ? [0] : countList.reduce((a, b) => a + b);

            second.push({
                id: selected?.id,
                pos: purchases,
                shipping,
                totalQuantity
            });
        }

        return second;

    }, [pos, shipping])


    const [items, setItems] = useState((detail && detail.Items) ?? [])
    const [comments, setComments] = useState(detail.Order.Comment ?? "")
    const [state, setState] = useState(detail.Order.State ?? orderStates[0].value)

    const formatForExport = useMemo(() => {
        return detail.Items.map(item => ({...item, qtBough: item.Quantity.Bought, qtOrdered: item.Quantity.Ordered,}))
    }, [detail])

    const handleClosing = () => {
        onClose && onClose()
    }

    const handleStateChange = (newState) => setState(newState)

    const handleEdit = () => setIsUpdateModalShown(true)

    const handleEditModalClosing = () => setIsUpdateModalShown(false)

    const handleDeletion = () => setIsDeleteModalOpen(true)

    const handleDeleteModalClosing = () => setIsDeleteModalOpen(false)

    const handleDeleteConfirmation = () =>
        dispatch(deleteOrder({
            orderId: detail.id, onResolve: () => {
                handleClosing()
            }
        }))

    const handleSetQuantity = (index, quantity) =>
        setItems(items.map((item, idx) => {
            return (idx === index)
                ? ({...item, Quantity: {...item.Quantity, Bought: quantity}})
                : item
        }))

    const handleApply = () => {

        setIsLoading(true)

        dispatch(updateOrder({
            order: {...detail, Items: items, Order: {...detail.Order, State: state, Comment: comments,}},
            onResolve: () => {
                setIsLoading(false)
                handleClosing()
            }
        }))

    }

    const handleCommentsChange = (comments) => {
        setComments(comments)
    }


    return (
        <>
            <Dialog open={open} onClose={handleClosing} maxWidth='md' fullWidth>
                <DialogTitle>
                    <Row>
                        <Col className='col-auto text-truncate' style={{display: 'grid', placeItems: 'center'}}>
                            <Row>
                                <Col className='col-auto'>
                                    {`C ${"0".repeat(8 - String(detail.Order.Number).length)}${detail.Order.Number}`} |
                                    "{detail.Purchaser.Name}"
                                </Col>
                                <Col>
                                    <Tooltip title={isLate ? "En retard" : "Il reste encore du temps"}>
                                        <Chip
                                            label={deadline.toFormat('dd-MM-yyyy')}
                                            color={(isLate) ? "secondary" : "default"}
                                        />
                                    </Tooltip>
                                </Col>
                            </Row>
                        </Col>
                        <Col className='col-auto ml-auto'>
                            <ExcelFile
                                element={(
                                    <Tooltip title={"Exporter sous Exel"}>
                                        <IconButton>
                                            <GridOnIcon/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            >
                                <ExcelSheet data={formatForExport} name="Orders">
                                    <ExcelColumn label="Firebase Id" value="Id"/>
                                    <ExcelColumn label="ProSoft Id" value="InternalId"/>
                                    <ExcelColumn label="Barcode" value="Barcode"/>
                                    <ExcelColumn label="Name" value="Name"/>
                                    <ExcelColumn label="Ordered Quantity" value="qtOrdered"/>
                                    <ExcelColumn label="Bought Quantity" value="qtBough"/>
                                </ExcelSheet>
                            </ExcelFile>
                            <Tooltip title={"Modifer L'ordre"}>
                                <IconButton onClick={handleEdit}>
                                    <EditIcon/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={"Supprimer L'ordre"}>
                                <IconButton onClick={handleDeletion}>
                                    <RemoveIcon color='error'/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={"Fermer"}>
                                <IconButton onClick={handleClosing}>
                                    <CloseIcon/>
                                </IconButton>
                            </Tooltip>
                        </Col>
                    </Row>
                </DialogTitle>
                <DialogContent>

                    <Row>
                        <Col style={{display: "grid", placeItems: 'center'}}>
                            {
                                items && items.map(({ImageUrl, Name, Id, Quantity, ...rest}, index) => {

                                    const metadata = meta?.find(one=>one?.id===Id);

                                    return (
                                        <Row key={Id} style={{marginBottom: 5}}>
                                            <Card style={{width: 920}}>
                                                <Card.Body style={{padding: 0}}>
                                                    <Row>
                                                        <Col className='col-auto pt-1 pl-4'>
                                                            <Avatar src={ImageUrl}/>
                                                        </Col>
                                                        <Col style={{display: 'grid', placeItems: 'center'}}>
                                                            <b>{Name}</b>
                                                        </Col>

                                                        <Col className='col-auto ml-auto'>
                                                            <Row>
                                                                <small>Po & ship.</small>
                                                            </Row>
                                                            <InventoryOrdered {...metadata}/>
                                                        </Col>

                                                        <Col className='col-auto ml-auto'>
                                                            <Row>
                                                                <small>Quantité commandée</small>
                                                            </Row>
                                                            <Row className='mt-2'>
                                                                <h6>{Quantity.Ordered}</h6>
                                                            </Row>
                                                        </Col>

                                                        <div className='m-2'/>

                                                        <Col className='col-auto ml-auto '>
                                                            <TextField
                                                                label='Quantité Achetée'
                                                                type='number'
                                                                value={Quantity.Bought}
                                                                min={0}
                                                                variant='outlined' margin='dense'
                                                                disabled={isLoading}
                                                                style={{width: 130, marginRight: 10}}
                                                                onChange={event => handleSetQuantity(index, Number(event.target.value))}
                                                            />
                                                        </Col>

                                                    </Row>
                                                </Card.Body>
                                            </Card>

                                        </Row>
                                    );
                                })
                            }
                        </Col>

                    </Row>

                    <Row>
                        <Col style={{display: "grid", placeItems: 'center'}}>
                            <Card>
                                <Card.Header>Commentaires</Card.Header>
                                <Card.Body>
                                    <TextareaAutosize rowsMin={2} rowsMax={5} style={{width: 900}} value={comments}
                                                      onChange={event => handleCommentsChange(event.target.value)}/>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>


                </DialogContent>

                <DialogActions>

                    <FormControl className={classes.formControl}>
                        <InputLabel>Etat</InputLabel>
                        <Select
                            value={state}
                            onChange={event => handleStateChange(event.target.value)}
                            disabled={isLoading}
                        >
                            {
                                orderStates.map(state => (
                                    <MenuItem key={nanoid()} value={state.value}>{state.label}</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                    <Button variant={"contained"} color='primary' onClick={handleApply} disabled={isLoading}>
                        Appliquer
                    </Button>
                </DialogActions>

            </Dialog>
            <UpdateOrders show={isUpdateModalShown} orderData={detail} onClose={handleEditModalClosing}/>
            <DeleteModal
                open={isDeleteModalOpen}
                onClose={handleDeleteModalClosing}
                onConfirm={handleDeleteConfirmation}
            />
        </>
    )
}